/**
 * ScienceSection.tsx
 * ──────────────────
 * The "Science" / proof section of the LP Express long-form landing page.
 * Sits between the Alternatives section (S11) and the Functional Skincare
 * section (S6). Every piece of copy is silent-fallback: when a field from the
 * lp-proof-copy workflow is absent/empty the corresponding UI renders nothing
 * (no hardcoded template copy). The research-board header image comes from the
 * lp-proof-images workflow ("research-board" slot); when it is absent or its
 * FAL render fails the image slot is left empty (no hardcoded fallback image).
 */

import React, { useState } from "react";
import { useLpStatusDots } from "./lp-status-dots";
import { CheckCircle2, X, FlaskConical, Loader2, ArrowRight, RotateCw } from "lucide-react";
import { motion } from "framer-motion";
import { LpImage } from "@/components/LpMediaPlaceholder";
import { computeReceipt, type LpReceipt } from "@/lib/lp-express-pricing";
import { FarmstandReceiptBox } from "@/components/RemainingPageSections";
import { EditableText, EditableLink, EditableElement, RegenerateButton, LpReorderGroup, LpImageActions, useLpMediaOverride } from "./lp-editor";

export type ProofLink = { label?: string; url?: string };
export type ProofCopy = {
  section_eyebrow?: string;
  section_headline?: string;
  section_subheadline?: string;
  proof_folder_headline?: string;
  proof_folder_body?: string;
  proof_folder_bullets?: string[];
  proof_cards?: Array<{
    name?: string;
    what_it_is?: string;
    what_it_does?: string;
    why_it_matters?: string;
    research_links?: ProofLink[];
  }>;
  why_it_matters_headline?: string;
  why_it_matters_body?: string;
  why_it_matters_links?: ProofLink[];
  comparison_them?: string[];
  comparison_us?: string[];
  sources_label?: string;
  sources_note?: string;
  disclaimer?: string;
};

type LpImgSlot = {
  key?: string;
  imageUrl?: string;
  altText?: string;
  imageStatus?: string;
  imageLoading?: boolean;
};

// First non-empty trimmed string wins; else "". No hardcoded fallbacks are
// passed here — callers only supply workflow values.
function pick(...vals: Array<string | undefined | null>): string {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return "";
}

// Clean a research-link list down to entries that have a usable label/url.
// `storedIndex` is the item's index in the RAW array so an in-canvas URL edit
// can target the exact stored entry (cleanLinks drops empty entries).
function cleanLinks(
  links?: ProofLink[],
): Array<{ label: string; url: string; storedIndex: number }> {
  if (!Array.isArray(links)) return [];
  return links
    .map((l, storedIndex) => ({ label: pick(l?.label, l?.url), url: pick(l?.url), storedIndex }))
    .filter((l) => l.label || l.url);
}

// A usable tag pill is a short scientific category ("Emollient", "Lipid Base"),
// not a full descriptive sentence. When the workflow returns a sentence for
// what_it_is, we omit the pill rather than stretch it across the card.
function isShortTag(s?: string): boolean {
  const t = (s || "").trim();
  if (!t) return false;
  return t.split(/\s+/).length <= 3 && t.length <= 28 && !/[.!?]$/.test(t);
}

// Render inline **bold** markdown spans (mirrors the other LP sections).
function renderBold(text: string): React.ReactNode {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} style={{ fontWeight: 800, color: "var(--color-brand-deepnavy)" }}>
        {part}
      </strong>
    ) : (
      part
    ),
  );
}

function ResearchLinks({
  links,
  basePath,
}: {
  links?: ProofLink[];
  /** Path to the raw links array in generatedContent (e.g.
   *  ["proof","why_it_matters_links"]). Each link's url/label paths are derived
   *  from it + the entry's storedIndex so in-canvas edits hit the right entry. */
  basePath: (string | number)[];
}) {
  const items = cleanLinks(links);
  if (items.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, marginTop: 14 }}>
      {items.map((l, i) => (
        <EditableLink
          key={l.storedIndex}
          path={[...basePath, l.storedIndex, "url"]}
          labelPath={[...basePath, l.storedIndex, "label"]}
          url={l.url}
          label={l.label}
          testId={`research-${i}`}
          trailing={<ArrowRight style={{ width: 13, height: 13, flexShrink: 0 }} />}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 7,
            padding: "8px 13px",
            backgroundColor: "#EEF0FB",
            borderRadius: 9,
            fontSize: 12,
            fontWeight: 700,
            color: "var(--color-brand-indigo)",
            textDecoration: "none",
            lineHeight: 1.2,
          }}
        />
      ))}
    </div>
  );
}

// Retry affordance for the failed research-board image, rendered inside the
// browser-window chrome (in-app builder only). Clicking regenerates just this
// one slot.
function ResearchBoardRetry({ onRetry }: { onRetry: (slotKey: string) => void }) {
  const [clicked, setClicked] = useState(false);
  return (
    <div
      data-testid="retry-science-research-board"
      style={{
        display: "flex",
        flexDirection: "column" as const,
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        height: 300,
        backgroundColor: "#F0EDEA",
      }}
    >
      <span style={{ fontSize: 12, fontWeight: 700, color: "#8A7F68" }}>
        Image failed to generate
      </span>
      <button
        type="button"
        disabled={clicked}
        onClick={() => {
          setClicked(true);
          onRetry("research-board");
        }}
        data-testid="button-retry-science-research-board"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "8px 14px",
          borderRadius: 999,
          border: "1px solid var(--color-brand-navyaccent)",
          backgroundColor: "#FFFFFF",
          color: "var(--color-brand-navyaccent)",
          fontSize: 13,
          fontWeight: 700,
          cursor: clicked ? "default" : "pointer",
          opacity: clicked ? 0.6 : 1,
        }}
      >
        <RotateCw style={{ width: 14, height: 14 }} className={clicked ? "animate-spin" : ""} />
        {clicked ? "Retrying…" : "Retry"}
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SCIENCE / PROOF SECTION
───────────────────────────────────────────── */

export function ScienceSection({
  proof,
  loading = false,
  images,
  imagesLoading = false,
  receipt,
  brandName,
  bundleImage,
  bundleProductPhotos,
  checkoutUrl,
  onRetry,
}: {
  proof?: ProofCopy;
  loading?: boolean;
  images?: LpImgSlot[];
  imagesLoading?: boolean;
  receipt?: LpReceipt;
  brandName?: string | null;
  bundleImage?: string | null;
  bundleProductPhotos?: string[];
  checkoutUrl?: string | null;
  // Retry the failed research-board image slot (in-app builder only). Undefined
  // on the public/published page so a failed slot just stays empty.
  onRetry?: (slotKey: string) => void;
}) {
  const r: LpReceipt = receipt ?? computeReceipt([], undefined, null);
  const buyBoxOverride = useLpMediaOverride("product:science-cta");
  const buyBoxImage = buyBoxOverride ?? bundleImage;

  const eyebrow = pick(proof?.section_eyebrow);
  const headline = pick(proof?.section_headline);
  const subheadline = pick(proof?.section_subheadline);

  const folderHeadline = pick(proof?.proof_folder_headline);
  const folderBody = pick(proof?.proof_folder_body);
  const folderBullets = Array.isArray(proof?.proof_folder_bullets)
    ? proof!.proof_folder_bullets!
        .map((b, idx) => ({ text: pick(b), storedIndex: idx }))
        .filter((b) => b.text)
    : [];

  const cards = Array.isArray(proof?.proof_cards)
    ? proof!.proof_cards!
        .map((c, idx) => ({
          name: pick(c?.name),
          what_it_is: pick(c?.what_it_is),
          what_it_does: pick(c?.what_it_does),
          why_it_matters: pick(c?.why_it_matters),
          research_links: c?.research_links,
          storedIndex: idx,
        }))
        .filter(
          (c) =>
            c.name ||
            c.what_it_is ||
            c.what_it_does ||
            c.why_it_matters ||
            cleanLinks(c.research_links).length > 0,
        )
    : [];

  const whyHeadline = pick(proof?.why_it_matters_headline);
  const whyBody = pick(proof?.why_it_matters_body);

  const them = Array.isArray(proof?.comparison_them)
    ? proof!.comparison_them!
        .map((t, idx) => ({ text: pick(t), storedIndex: idx }))
        .filter((t) => t.text)
    : [];
  const us = Array.isArray(proof?.comparison_us)
    ? proof!.comparison_us!
        .map((t, idx) => ({ text: pick(t), storedIndex: idx }))
        .filter((t) => t.text)
    : [];

  const disclaimer = pick(proof?.disclaimer);
  const sourcesLabel = pick(proof?.sources_label);
  const sourcesNote = pick(proof?.sources_note);

  // Research-board header image: render the durable image, a spinner while the
  // pipeline is in flight, or NOTHING — never a hardcoded fallback image.
  const boardSlot = images?.find((x) => x?.key === "research-board");
  const boardUrl = boardSlot?.imageUrl;
  const showStatusDots = useLpStatusDots();
  // Only spin for THIS slot's own state. `imagesLoading` is the aggregate
  // proof-images flag (also true while the unrelated material-texture slot
  // renders), so we only fall back to it before the slots exist yet. Once the
  // research-board slot is present, its own status decides: "none"/"failed"
  // must render nothing, never a perpetual spinner.
  const boardPending =
    !boardUrl &&
    (boardSlot?.imageLoading ||
      boardSlot?.imageStatus === "pending" ||
      (imagesLoading && !boardSlot));
  // In-app builder only: a failed slot gets a one-click retry instead of just
  // disappearing. On the public page (onRetry undefined) it stays empty.
  const boardFailed =
    !boardUrl && !boardPending && !!onRetry && boardSlot?.imageStatus === "failed";

  const hasHeader = !!(eyebrow || headline || subheadline) || loading;

  return (
    <section
      style={{ backgroundColor: "#FAFAF7", borderTop: "1px solid #E8E0D4", padding: "56px 0" }}
      data-testid="section-science"
    >
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "0 20px" }}>
        {/* (1) Header */}
        {hasHeader && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45 }}
            style={{ textAlign: "center" as const, marginBottom: 28 }}
          >
            {eyebrow && (
              <div
                style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 12 }}
                data-testid="text-science-eyebrow"
              >
                <FlaskConical style={{ width: 14, height: 14, color: "var(--color-brand-navyaccent)" }} />
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 900,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase" as const,
                    color: "var(--color-brand-navyaccent)",
                  }}
                >
                  <EditableText as="span" elementKey="science:eyebrow" path={["proof", "section_eyebrow"]} value={eyebrow} placeholder="The Science" />
                </span>
              </div>
            )}
            {headline ? (
              <h2
                style={{ fontSize: 30, lineHeight: 1.15, fontWeight: 900, color: "var(--color-brand-deepnavy)", margin: 0 }}
                data-testid="text-science-headline"
              >
                <EditableText as="span" elementKey="science:headline" path={["proof", "section_headline"]} value={headline} placeholder="" multiline />
              </h2>
            ) : loading ? (
              <div style={{ display: "flex", justifyContent: "center" }} data-testid="loading-science-header">
                <Loader2 className="h-6 w-6 animate-spin" style={{ color: "var(--color-brand-navyaccent)" }} />
              </div>
            ) : null}
            {subheadline && (
              <p
                style={{ fontSize: 15, lineHeight: 1.6, color: "#5A554F", marginTop: 12, maxWidth: 540, marginLeft: "auto", marginRight: "auto" }}
                data-testid="text-science-subheadline"
              >
                <EditableText as="span" elementKey="science:subheadline" path={["proof", "section_subheadline"]} value={subheadline} placeholder="" multiline />
              </p>
            )}
          </motion.div>
        )}

        <LpReorderGroup
          groupKey="proof:blocks"
          items={[
        /* Research-board header image — empty when absent; a retry box when
            failed (in-app builder only). */
        ...((boardUrl || boardPending || boardFailed) ? [{ key: "science:research-board-image", node: (
          <EditableElement elementKey="science:research-board-image" toolbarSide="left" testId="element-science-research-board">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45 }}
            style={{ marginBottom: 36 }}
          >
            <div
              style={{
                borderRadius: 12,
                overflow: "hidden",
                border: "1px solid #E8E0D4",
                backgroundColor: "#F0EDEA",
                boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
              }}
            >
              {/* Browser-window chrome */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "8px 12px",
                  backgroundColor: "#F0EDEA",
                  borderBottom: "1px solid #E0DAD0",
                }}
              >
                {["var(--color-brand-coral)", "var(--color-brand-goldaccent)", "var(--color-brand-greenaccent)"].map((c) => (
                  <span key={c} style={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: c }} />
                ))}
              </div>
              {boardUrl ? (
                <div style={{ position: "relative" as const }}>
                  <img
                    src={boardUrl}
                    alt={boardSlot?.altText || "Research board"}
                    data-testid="img-science-research-board"
                    style={{ display: "block", width: "100%", height: 300, objectFit: "cover" }}
                  />
                  {onRetry && <RegenerateButton onClick={() => onRetry("research-board")} testId="button-regenerate-science-research-board" />}
                  {showStatusDots && (
                    <span
                      title="AI-generated image"
                      aria-label="AI-generated image"
                      data-testid="dot-ai-image-research-board"
                      style={{
                        position: "absolute",
                        top: 12,
                        right: 12,
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: "#22C55E",
                        boxShadow: "0 0 0 3px rgba(255,255,255,0.7), 0 1px 3px rgba(0,0,0,0.3)",
                      }}
                    />
                  )}
                </div>
              ) : boardPending ? (
                <div
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", height: 300 }}
                  data-testid="loading-science-research-board"
                >
                  <Loader2 className="h-7 w-7 animate-spin" style={{ color: "#6B6560" }} />
                </div>
              ) : (
                onRetry && <ResearchBoardRetry onRetry={onRetry} />
              )}
            </div>
          </motion.div>
          </EditableElement>
        ) }] : []),
        /* (2) "How It Works" manila research folder — whole card is one hide/delete unit */
        ...((folderHeadline || folderBody || folderBullets.length > 0) ? [{ key: "science:how-it-works-folder", node: (
          <EditableElement elementKey="science:how-it-works-folder" testId="element-science-how-it-works">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45 }}
            style={{ marginBottom: 36 }}
            data-testid="block-science-how-it-works"
          >
            <div style={{ display: "flex" }}>
              <EditableText
                as="span"
                style={{
                  display: "inline-block",
                  padding: "6px 18px",
                  backgroundColor: "#D9CEAF",
                  borderRadius: "10px 10px 0 0",
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase" as const,
                  color: "#5C5230",
                }}
                formatOnly
                path={["proof", "folder_tab_label"]}
                value={(proof as any)?.folder_tab_label}
                placeholder="How It Works"
              />
            </div>
            <div
              style={{
                backgroundColor: "#D4C4A0",
                borderRadius: "0 12px 12px 12px",
                padding: 6,
              }}
            >
              <div style={{ backgroundColor: "#FFFDF8", borderRadius: 8, padding: "22px 22px" }}>
                {folderHeadline && (
                  <h3
                    style={{ fontSize: 19, fontWeight: 800, color: "var(--color-brand-deepnavy)", margin: 0, marginBottom: 12 }}
                    data-testid="text-science-folder-headline"
                  >
                    <EditableText as="span" formatOnly path={["proof", "proof_folder_headline"]} value={folderHeadline} placeholder="" multiline />
                  </h3>
                )}
                {folderBullets.length > 0 && (
                  <div style={{ marginBottom: folderBody ? 14 : 0 }}>
                    {folderBullets.map((b, i) => (
                      <div
                        key={i}
                        style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 8 }}
                        data-testid={`text-science-folder-bullet-${i}`}
                      >
                        <CheckCircle2 style={{ width: 15, height: 15, color: "var(--color-brand-greenaccent)", flexShrink: 0, marginTop: 2 }} />
                        <EditableText as="span" formatOnly style={{ fontSize: 13.5, lineHeight: 1.5, color: "#3A352F", fontWeight: 600 }} path={["proof", "proof_folder_bullets", b.storedIndex]} value={b.text} placeholder="" multiline />
                      </div>
                    ))}
                  </div>
                )}
                {folderBody && (
                  <p
                    style={{ fontSize: 14, lineHeight: 1.65, color: "#5A554F", margin: 0 }}
                    data-testid="text-science-folder-body"
                  >
                    <EditableText as="span" formatOnly path={["proof", "proof_folder_body"]} value={folderBody} placeholder="" multiline />
                  </p>
                )}
              </div>
            </div>
          </motion.div>
          </EditableElement>
        ) }] : []),
        /* (3) Proof "browser window" cards — each card is one hide/delete unit */
        ...(cards.length > 0 ? [{ key: "science:proof-cards", node: (
          <div style={{ display: "grid", gap: 16, marginBottom: 36 }}>
            <LpReorderGroup
              groupKey="science:proof-card-list"
              items={cards.map((card, idx) => ({ key: `science:proof-card-${card.storedIndex}`, node: (
              <EditableElement key={idx} elementKey={`science:proof-card-${card.storedIndex}`} testId={`element-science-proof-${idx}`}>
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: Math.min(idx * 0.05, 0.2) }}
                style={{
                  borderRadius: 12,
                  overflow: "hidden",
                  border: "1px solid #E5DED2",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.04)",
                }}
                data-testid={`card-science-proof-${idx}`}
              >
                {/* browser chrome — traffic-light dots only */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "9px 13px",
                    backgroundColor: "#F0EDEA",
                    borderBottom: "1px solid #E0DAD0",
                  }}
                >
                  {["var(--color-brand-coral)", "var(--color-brand-goldaccent)", "var(--color-brand-greenaccent)"].map((c) => (
                    <span key={c} style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: c }} />
                  ))}
                </div>
                <div style={{ padding: "20px 20px" }}>
                  {(isShortTag(card.what_it_is) || card.name) && (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" as const, marginBottom: 12 }}>
                      {isShortTag(card.what_it_is) && (
                        <EditableText
                          as="span"
                          style={{
                            display: "inline-block",
                            padding: "4px 10px",
                            backgroundColor: "#EEF0FB",
                            borderRadius: 7,
                            fontSize: 12,
                            fontWeight: 700,
                            color: "var(--color-brand-indigo)",
                          }}
                          formatOnly
                          path={["proof", "proof_cards", card.storedIndex, "what_it_is"]}
                          value={card.what_it_is}
                          placeholder=""
                          testId={`text-science-card-tag-${idx}`}
                        />
                      )}
                      {card.name && (
                        <EditableText
                          as="span"
                          style={{ fontSize: 18, fontWeight: 800, color: "var(--color-brand-deepnavy)", lineHeight: 1.2 }}
                          formatOnly
                          path={["proof", "proof_cards", card.storedIndex, "name"]}
                          value={card.name}
                          placeholder=""
                          testId={`text-science-card-name-${idx}`}
                        />
                      )}
                    </div>
                  )}
                  {card.what_it_does && (
                    <EditableText
                      as="p"
                      style={{ fontSize: 14.5, lineHeight: 1.65, color: "#4A4540", margin: 0 }}
                      formatOnly
                      path={["proof", "proof_cards", card.storedIndex, "what_it_does"]}
                      value={card.what_it_does}
                      placeholder=""
                      render={renderBold}
                      multiline
                      testId={`text-science-card-does-${idx}`}
                    />
                  )}
                  {card.why_it_matters && (
                    <EditableText
                      as="p"
                      style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--color-brand-navyaccent)", fontWeight: 700, margin: 0, marginTop: 14 }}
                      formatOnly
                      path={["proof", "proof_cards", card.storedIndex, "why_it_matters"]}
                      value={card.why_it_matters}
                      placeholder=""
                      render={renderBold}
                      multiline
                      testId={`text-science-card-why-${idx}`}
                    />
                  )}
                  <ResearchLinks links={card.research_links} basePath={["proof", "proof_cards", card.storedIndex, "research_links"]} />
                </div>
              </motion.div>
              </EditableElement>
              ) }))}
            />
          </div>
        ) }] : []),
        /* (4) "Why It Matters" second manila folder — whole card is one hide/delete unit */
        ...((whyHeadline || whyBody || cleanLinks(proof?.why_it_matters_links).length > 0) ? [{ key: "science:why-it-matters-folder", node: (
          <EditableElement elementKey="science:why-it-matters-folder" testId="element-science-why-it-matters">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45 }}
            style={{ marginBottom: 36 }}
            data-testid="block-science-why-it-matters"
          >
            <div style={{ display: "flex" }}>
              <EditableText
                as="span"
                style={{
                  display: "inline-block",
                  padding: "6px 18px",
                  backgroundColor: "#D9CEAF",
                  borderRadius: "10px 10px 0 0",
                  fontSize: 11,
                  fontWeight: 900,
                  letterSpacing: "0.1em",
                  textTransform: "uppercase" as const,
                  color: "#5C5230",
                }}
                formatOnly
                path={["proof", "why_tab_label"]}
                value={(proof as any)?.why_tab_label}
                placeholder="Why It Matters"
              />
            </div>
            <div style={{ backgroundColor: "#D4C4A0", borderRadius: "0 12px 12px 12px", padding: 6 }}>
              <div style={{ backgroundColor: "#FFFDF8", borderRadius: 8, padding: "22px 22px" }}>
                {whyHeadline && (
                  <h3
                    style={{ fontSize: 19, fontWeight: 800, color: "var(--color-brand-deepnavy)", margin: 0, marginBottom: 10 }}
                    data-testid="text-science-why-headline"
                  >
                    <EditableText as="span" formatOnly path={["proof", "why_it_matters_headline"]} value={whyHeadline} placeholder="" multiline />
                  </h3>
                )}
                {whyBody && (
                  <p
                    style={{ fontSize: 14, lineHeight: 1.65, color: "#5A554F", margin: 0 }}
                    data-testid="text-science-why-body"
                  >
                    <EditableText as="span" formatOnly path={["proof", "why_it_matters_body"]} value={whyBody} placeholder="" multiline />
                  </p>
                )}
                <ResearchLinks links={proof?.why_it_matters_links} basePath={["proof", "why_it_matters_links"]} />
              </div>
            </div>
          </motion.div>
          </EditableElement>
        ) }] : []),
        /* (5) Comparison strip — whole card is one hide/delete unit */
        ...((them.length > 0 || us.length > 0) ? [{ key: "science:comparison-strip", node: (
          <EditableElement elementKey="science:comparison-strip" testId="element-science-comparison">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.45 }}
            style={{ marginBottom: 36 }}
            data-testid="block-science-comparison"
          >
            <div
              style={{
                borderRadius: 14,
                overflow: "hidden",
                border: "1px solid #E5DED2",
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
              }}
            >
              <div style={{ backgroundColor: "#F5F0EA", padding: "20px 16px" }}>
                <EditableText
                  as="p"
                  style={{
                    fontSize: 9,
                    fontWeight: 900,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase" as const,
                    color: "#A0564F",
                    marginBottom: 12,
                    textAlign: "center" as const,
                  }}
                  formatOnly
                  path={["proof", "them_label"]}
                  value={(proof as any)?.them_label}
                  placeholder="Most skincare"
                />
                {them.map((t, i) => (
                  <div
                    key={i}
                    style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 8 }}
                    data-testid={`text-science-them-${i}`}
                  >
                    <X style={{ width: 13, height: 13, color: "#C0564F", flexShrink: 0, marginTop: 2 }} />
                    <EditableText as="span" formatOnly style={{ fontSize: 11.5, color: "#6B5450", fontWeight: 600, lineHeight: 1.35 }} path={["proof", "comparison_them", t.storedIndex]} value={t.text} placeholder="" multiline />
                  </div>
                ))}
              </div>
              <div style={{ backgroundColor: "#FFFFFF", padding: "20px 16px" }}>
                <EditableText
                  as="p"
                  style={{
                    fontSize: 9,
                    fontWeight: 900,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase" as const,
                    color: "var(--color-brand-greenaccent)",
                    marginBottom: 12,
                    textAlign: "center" as const,
                  }}
                  formatOnly
                  path={["proof", "us_label"]}
                  value={(proof as any)?.us_label}
                  placeholder="This kit"
                />
                {us.map((t, i) => (
                  <div
                    key={i}
                    style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 8 }}
                    data-testid={`text-science-us-${i}`}
                  >
                    <CheckCircle2 style={{ width: 13, height: 13, color: "var(--color-brand-greenaccent)", flexShrink: 0, marginTop: 2 }} />
                    <EditableText as="span" formatOnly style={{ fontSize: 11.5, color: "#2A4A38", fontWeight: 600, lineHeight: 1.35 }} path={["proof", "comparison_us", t.storedIndex]} value={t.text} placeholder="" multiline />
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
          </EditableElement>
        ) }] : []),
        /* Trust badge — driven entirely by the workflow (sources_label /
            sources_note). Hidden when the workflow returns neither, so we never
            assert a source claim that isn't true for the brand. */
        ...((sourcesLabel || sourcesNote) ? [{ key: "science:trust-badge", node: (
          <EditableElement elementKey="science:trust-badge" testId="element-science-trust-badge">
          <div style={{ display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 6, marginBottom: 28 }}>
            {sourcesLabel && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 7,
                  padding: "8px 16px",
                  backgroundColor: "#EEF6F1",
                  border: "1px solid #CFE6DA",
                  borderRadius: 999,
                }}
                data-testid="badge-science-trust"
              >
                <ShieldDot />
                <span style={{ fontSize: 11, fontWeight: 700, color: "var(--color-brand-greenaccent)", letterSpacing: "0.02em" }}>
                  <EditableText as="span" formatOnly path={["proof", "sources_label"]} value={sourcesLabel} placeholder="" />
                </span>
              </div>
            )}
            {sourcesNote && (
              <p
                style={{ fontSize: 11, color: "#6B6560", margin: 0, textAlign: "center" as const }}
                data-testid="text-science-trust-note"
              >
                <EditableText as="span" formatOnly path={["proof", "sources_note"]} value={sourcesNote} placeholder="" multiline />
              </p>
            )}
          </div>
          </EditableElement>
        ) }] : []),
        /* Receipt buy box (reuses the shared FarmstandReceiptBox pattern) — whole card is one hide/delete unit */
        { key: "science:buy-box", node: (
        <EditableElement elementKey="science:buy-box" toolbarSide="left" testId="element-science-buy-box">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-30px" }}
          transition={{ duration: 0.5 }}
          data-testid="block-science-buy-box"
        >
          <div
            className="group/lpimg relative"
            style={{
              borderRadius: 14,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              overflow: "hidden",
              border: "2px solid #D8CFC3",
              borderBottom: "none",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            }}
          >
            <LpImage
              src={buyBoxImage}
              alt="Product bundle"
              path="/bundle-flatlay.jpeg"
              rounded={false}
              objectFit="contain"
              style={{ height: 240, backgroundColor: "#FAF6EE" }}
            />
            <LpImageActions
              slotKey="product:science-cta"
              productPhotos={bundleProductPhotos}
              revertLabel="Use default"
              revertTitle="Use the default product photo"
            />
          </div>
          <FarmstandReceiptBox receipt={r} brandName={brandName} checkoutUrl={checkoutUrl} />
        </motion.div>
        </EditableElement>
        ) },
        ]}
        />

        {/* Disclaimer */}
        {disclaimer && (
          <EditableText
            as="p"
            style={{ fontSize: 10.5, lineHeight: 1.55, color: "#9A958E", textAlign: "center" as const, marginTop: 22, maxWidth: 560, marginLeft: "auto", marginRight: "auto" }}
            path={["proof", "disclaimer"]}
            value={disclaimer}
            placeholder=""
            multiline
            testId="text-science-disclaimer"
          />
        )}
      </div>
    </section>
  );
}

function ShieldDot() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 16,
        height: 16,
        borderRadius: "50%",
        backgroundColor: "var(--color-brand-greenaccent)",
        flexShrink: 0,
      }}
    >
      <CheckCircle2 style={{ width: 11, height: 11, color: "#FFFFFF" }} />
    </span>
  );
}
